import { App, BlockedResourceError, NotFoundError, UserVerificationPendingError, AppStatusError, AppStatus, PaymentPendingError, DeploymentStatusError, AuthorizationError, CustomDomainStatus } from "@back4app2/sdk";
import PreDeployConfirmModal from './PreDeployConfirmModal';
import { RunAssistance } from '@back4app2/sdk';
import { useState } from "react";
import back4app2 from "../back4app2";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { hasWritePermission } from "../utils/permissions";
import { DsButton } from "./ds";
import { TOAST_SURFACE } from "./ds/toast";

/**
 * Runs the analysis, then decides what the Deploy button does.
 *
 * Wired only into the explicit Deploy actions, not into "save settings and
 * redeploy" or "domain changed, redeploy": someone who has just configured the
 * app does not need to be asked about its configuration, and a screen that
 * appears on every path is one nobody reads.
 */
const runPreDeploy = async (serviceEnvironmentId: string) => {
  try {
    return await back4app2.prepareDeployment(serviceEnvironmentId);
  } catch (e) {
    // Never block the deploy on the pre-check: it decides what to SHOW, and
    // the worker gate is what actually enforces. Failing here would turn an
    // analyzer problem into an inability to deploy.
    return { blocked: false, needsConfirmation: false };
  }
};

/**
 * Whether there is an action to offer at all. Where the answer is no the
 * component draws nothing, and the row under the status card closes up.
 */
const canRunAppActions = (app: App): boolean => {
  const env = app.mainService && app.mainService.mainServiceEnvironment;
  if (!env) {
    return false;
  }
  // A blocked app has exactly one thing to do and deploying is not it: the
  // block card on the overview carries the explanation and the appeal. The
  // pre-deploy check refuses these anyway, one toast later.
  if (app.block) {
    return false;
  }
  if ([AppStatus.INITIALIZING, AppStatus.PENDING_VERIFICATION, AppStatus.FAILED].includes(app.status)) {
    return false;
  }
  if (env.isPendingPayment || (!env.isFreePlanElegible && env.plan.name.includes('Free'))) {
    return false;
  }
  return hasWritePermission(app, 'Deploy');
};

/**
 * The app's one action, as a button instead of a menu.
 *
 * What stood here was an "Action" dropdown holding a single item — the chevron,
 * the outside-click handler and the panel were all machinery around a list of
 * one, behind a word that never said what pressing it would do. The button is
 * the item.
 *
 * Two labels, one deploy. An expired URL is not a new commit waiting to ship:
 * it is this app's own address to get back, and the deploy that renews it reads
 * as Redeploy. Every other state ships the head of the branch and says so.
 */
const MyAppActions = ({ app, compact = false }: {
  app: App,
  /** Inline in the mobile header, where the full sentence does not fit beside
   * the app name — it goes on the button's title instead. */
  compact?: boolean,
}) => {
  const mainServiceEnvironment = app.mainService && app.mainService.mainServiceEnvironment;
  const navigate = useNavigate();

  const [isDeploying, setIsDeploying] = useState(false);
  const [pendingDeploy, setPendingDeploy] = useState<{
    serviceEnvironmentId: string;
    runAssistance: RunAssistance;
  } | null>(null);

  const deployLatestCommit = async () => {
    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    trackEvent(AmplitudeEvent.DEPLOY_LATEST_COMMIT);
    setIsDeploying(true);
    const toastId = toast.loading('Deploying latest commit...', {
      className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const prepared = await runPreDeploy(
        mainServiceEnvironment?.id as string
      );

      if (prepared.blocked) {
        // The block card on the overview carries the explanation and the
        // appeal; a toast would only repeat it worse.
        toast.dismiss(toastId);
        navigate(`/apps/${app.id}`);
        return;
      }

      if (prepared.needsConfirmation && prepared.runAssistance) {
        toast.dismiss(toastId);
        setPendingDeploy({
          serviceEnvironmentId: mainServiceEnvironment?.id as string,
          runAssistance: prepared.runAssistance,
        });
        return;
      }

      await back4app2.triggerManualDeployment(mainServiceEnvironment?.id as string);
      toast.success('New deployment created!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      navigate(`/apps/${app.id}`);
    } catch (err) {
      let errMsg = 'Something went wrong!';
      if (err instanceof AppStatusError || err instanceof UserVerificationPendingError || err instanceof BlockedResourceError || err instanceof PaymentPendingError || err instanceof NotFoundError || err instanceof DeploymentStatusError || err instanceof AuthorizationError) {
        errMsg = err.message;
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE.replace('max-w-xs', 'min-w-xs'),
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    } finally {
      setIsDeploying(false);
    }
  }

  if (!canRunAppActions(app)) {
    return null;
  }

  // Only EXPIRED. An expiring URL still resolves, so the app is not asking to
  // be redeployed yet — it is running, and the button says what it ships.
  const isUrlExpired = mainServiceEnvironment?.mainCustomDomain?.status === CustomDomainStatus.EXPIRED;
  const label = isUrlExpired ? 'Redeploy' : 'Deploy latest commit';

  // No layout of its own: the sidebar and the mobile header each place it.
  return <>
    <DsButton
      variant="primary"
      size={compact ? 'md' : 'lg'}
      className={compact ? '' : 'w-full'}
      title={label}
      isDisabled={isDeploying}
      onClick={deployLatestCommit}
    >
      {/* Sideways the header has room for the verb and nothing after it, so
          the sentence retreats to the title above. */}
      {isDeploying ? 'Deploying…' : (compact && !isUrlExpired ? 'Deploy' : label)}
    </DsButton>
    {pendingDeploy && app.mainService?.mainServiceEnvironment ? (
      <PreDeployConfirmModal
        appId={app.id}
        serviceEnvironment={app.mainService.mainServiceEnvironment}
        runAssistance={pendingDeploy.runAssistance}
        onCancel={() => setPendingDeploy(null)}
        onProceed={async () => {
          const { serviceEnvironmentId } = pendingDeploy;
          setPendingDeploy(null);
          await back4app2.triggerManualDeployment(serviceEnvironmentId);
          navigate(`/apps/${app.id}`);
        }}
      />
    ) : null}
  </>;
};

export default MyAppActions;
